import Text, { TEXT_VARIANT } from '@pixum/text';
import Button, { ButtonVariants } from '@pixum/button';
import styles from './OrderStatusDeliveryDetails.module.css';

export interface DeliveryDetailsProps {
  deliveryInfo: string | null;
  showTrackingInfo: boolean;
  trackingUrl?: string;
  trackingNumber?: string;
  orderTrackingButtonText?: string;
}

/**
 * OrderStatusDeliveryDetails - Displays delivery information and tracking details
 *
 * This component renders delivery date information and optionally displays tracking
 * details with clickable links to external tracking services (e.g., DHL, DeutschePost).
 *
 * @param {DeliveryDetailsProps} props - Component props
 * @param {string | null} props.deliveryInfo - Formatted delivery information text (e.g., "Delivery date: 2024-01-15")
 * @param {boolean} props.showTrackingInfo - Whether to display tracking information and buttons
 * @param {string} [props.trackingUrl] - External tracking URL (required when showTrackingInfo is true)
 * @param {string} [props.trackingNumber] - Tracking number to display as a link
 * @param {string} [props.orderTrackingButtonText] - CTA button text for tracking (from CMS)
 *
 * @returns {JSX.Element} Delivery details section with optional tracking information
 */
export function OrderStatusDeliveryDetails({
  deliveryInfo,
  showTrackingInfo,
  trackingUrl,
  trackingNumber,
  orderTrackingButtonText,
}: DeliveryDetailsProps) {
  return showTrackingInfo ? (
    <section className={styles.trackingDetails}>
      <Text variant={TEXT_VARIANT.CAPTION} className={styles.deliveryDescriptionWithTracking}>
        {deliveryInfo}
      </Text>
      <Button
        className={styles.trackingLink}
        id="tracking-number"
        variant={ButtonVariants.TEXT}
        href={trackingUrl}
        target="_blank"
        rel="noreferrer"
      >
        {trackingNumber}
      </Button>
      <Button
        className={styles.trackingButton}
        variant={ButtonVariants.PRIMARY}
        href={trackingUrl}
        target="_blank"
        rel="noreferrer"
        title={orderTrackingButtonText}
      >
        {orderTrackingButtonText}
      </Button>
    </section>
  ) : (
    <Text variant={TEXT_VARIANT.CAPTION} className={styles.deliveryDescription}>
      {deliveryInfo}
    </Text>
  );
}
